import * as geminiService from '../../services/gemini';
import { GenerationOutcomeUnknownError, lastRequestId } from '../../services/geminiTransport';
import type { Chapter, Ledger, LogEntry, ProjectMetadata, SequelOperation, StoryBible } from '../types';
import {
  appendOperationRequestId,
  buildReadyProjectState,
  buildSequelIdentity,
  ensureSequelOperation,
  ensureTargetProjectStub,
  loadSequelOperation,
  markSequelFailed,
  markSequelUnknownOutcome,
  markOperationStatus,
  persistBibleEvolutionResult,
  persistLedgerEvolutionResult,
  shouldRunBibleStep,
  shouldRunLedgerStep,
  type SequelInitContext,
} from './sequelInitialization.service';

export interface SequelRunResult {
  operation: SequelOperation;
  metadata: ProjectMetadata;
  fullState: ReturnType<typeof buildReadyProjectState>['fullState'];
  resumed: boolean;
  unknownOutcome: boolean;
}

function stripBlueprintOnlyFields(raw: StoryBible): StoryBible {
  const {
    requireBlueprintApproval,
    blueprintPipelineVersion,
    blueprintApproved,
    blueprintApprovedAt,
    blueprintSufficiency,
    blueprintFieldSources,
    blueprintSourceSnapshot,
    paidEntitlementId: _paidEntitlementId,
    entitlementAppliedAt: _entitlementAppliedAt,
    ...sequelSafe
  } = raw as StoryBible & Record<string, unknown>;
  return sequelSafe as StoryBible;
}

function finalizeBible(currentBible: StoryBible, evolved: StoryBible): StoryBible {
  const cleaned = stripBlueprintOnlyFields(evolved);
  return {
    ...cleaned,
    volume: currentBible.volume + 1,
    creditTier: 'free_preview',
    exportUnlocked: false,
    maxChaptersAllowed: 1,
    rewritesUsed: 0,
    chapterPlans: [],
    isClosingMode: false,
    seriesId: currentBible.seriesId || currentBible.canonId,
  };
}

export async function runSequelInitialization(
  ctx: SequelInitContext,
  hooks?: {
    onStep?: (message: string) => void;
  },
): Promise<SequelRunResult> {
  const { operationId } = buildSequelIdentity(ctx);
  const { operation: initialOp, created } = await ensureSequelOperation(ctx);
  let operation = (await loadSequelOperation(ctx.ownerUid, ctx.sourceProjectId, operationId)) || initialOp;
  const resumed = !created && operation.completedSteps.length > 0;

  await ensureTargetProjectStub(ctx, operation);

  if (operation.status === 'READY' && operation.evolvedLedger && operation.evolvedBible) {
    const ready = buildReadyProjectState(
      operation,
      operation.evolvedLedger,
      operation.evolvedBible as StoryBible,
      [],
    );
    return { operation, ...ready, resumed: true, unknownOutcome: false };
  }

  if (operation.status === 'CANCEL_REQUESTED') {
    throw new Error('Sequel setup was cancelled. Resume is disabled until reviewed.');
  }

  const reloadOperation = async (): Promise<SequelOperation> => {
    const latest = await loadSequelOperation(ctx.ownerUid, ctx.sourceProjectId, operation.operationId);
    if (!latest) return operation;
    if (latest.status === 'CANCEL_REQUESTED') {
      throw new Error('Sequel setup was cancelled. Resume is disabled until reviewed.');
    }
    return latest;
  };

  let evolvedLedger: Ledger | undefined = operation.evolvedLedger;
  let evolvedBible: StoryBible | undefined = operation.evolvedBible as StoryBible | undefined;

  try {
    if (shouldRunLedgerStep(operation)) {
      operation = await reloadOperation();
      hooks?.onStep?.('Evolving ledger for next volume…');
      await markOperationStatus(ctx.ownerUid, ctx.sourceProjectId, operation.operationId, 'EVOLVING_LEDGER');
      const ledgerResult = await geminiService.evolveLedgerForSequel(ctx.ledger, ctx.storyBible, ctx.chapters);
      if (lastRequestId) {
        await appendOperationRequestId(ctx.ownerUid, ctx.sourceProjectId, operation.operationId, lastRequestId);
      }
      operation = await persistLedgerEvolutionResult(
        ctx.ownerUid,
        ctx,
        operation,
        ledgerResult,
        lastRequestId,
      );
      evolvedLedger = ledgerResult;
    } else if (operation.evolvedLedger) {
      evolvedLedger = operation.evolvedLedger;
    }

    if (!evolvedLedger) {
      throw new Error('Ledger evolution result missing. Resume after verifying operation state.');
    }

    operation = await reloadOperation();

    if (shouldRunBibleStep(operation)) {
      hooks?.onStep?.('Evolving story blueprint for next volume…');
      await markOperationStatus(ctx.ownerUid, ctx.sourceProjectId, operation.operationId, 'EVOLVING_BIBLE');
      const rawBible = await geminiService.evolveBibleForSequel(ctx.storyBible);
      const bibleResult = finalizeBible(ctx.storyBible, rawBible as StoryBible);
      if (lastRequestId) {
        await appendOperationRequestId(ctx.ownerUid, ctx.sourceProjectId, operation.operationId, lastRequestId);
      }
      operation = await persistBibleEvolutionResult(
        ctx.ownerUid,
        ctx,
        operation,
        bibleResult,
        lastRequestId,
      );
      evolvedBible = bibleResult;
    } else if (operation.evolvedBible) {
      evolvedBible = operation.evolvedBible as StoryBible;
    }

    if (!evolvedBible) {
      throw new Error('Blueprint evolution result missing. Resume after verifying operation state.');
    }

    operation = await reloadOperation();

    const seedLogs: LogEntry[] = [
      {
        id: `log_${Date.now()}`,
        timestamp: new Date().toLocaleTimeString(),
        agent: 'System',
        message: `Volume ${evolvedBible.volume} initialized as Free Preview.`,
        type: 'success',
      },
    ];

    const ready = buildReadyProjectState(operation, evolvedLedger, evolvedBible, seedLogs);
    return { operation, ...ready, resumed, unknownOutcome: false };
  } catch (error: unknown) {
    if (error instanceof GenerationOutcomeUnknownError) {
      await markSequelUnknownOutcome(
        ctx.ownerUid,
        ctx.sourceProjectId,
        operation.operationId,
        error.requestId || lastRequestId,
      );
      throw error;
    }
    const code = error instanceof Error ? error.message : 'SEQUEL_INIT_FAILED';
    await markSequelFailed(
      ctx.ownerUid,
      ctx.sourceProjectId,
      operation.operationId,
      code,
      lastRequestId,
    );
    throw error;
  }
}

export async function querySequelOperationForResume(
  ctx: SequelInitContext,
): Promise<SequelOperation | null> {
  const { operationId } = buildSequelIdentity(ctx);
  return loadSequelOperation(ctx.ownerUid, ctx.sourceProjectId, operationId);
}
